import React, { useMemo } from "react";
import { colors } from '../../theme/colors';
import {
  Box,
  Chip,
  IconButton,
  ClickAwayListener,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearIcon from "@mui/icons-material/Clear";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { FixedSizeList } from "react-window";
import {
  LocationPickerWrapper,
  LocationInputField,
  LocationDropdown,
} from "../../pages/app/global-talent-search/style";
import GoogleMapBg from "../../assets/Images/Google Maps.jpg";

const MAX_VISIBLE_CHIPS = 2;

const GlobalTalentLocationPicker = ({
  disabled = false,
  multiple = false,
  locationInput,
  locationDropdownOpen,
  setLocationDropdownOpen,
  selectedCountry,
  countryOptions,
  citySearchQuery,
  setCitySearchQuery,
  loadingCities,
  filteredCities,
  handleCountrySelect,
  handleCitySelect,
  handleLocationDropdownClose,
  handleLocationClear,
  handleLocationRemove,
}) => {
  const hasValue = multiple
    ? Array.isArray(locationInput) && locationInput.length > 0
    : Boolean(locationInput);

  const selectedSet = useMemo(() => {
    if (!multiple || !Array.isArray(locationInput)) return new Set();
    return new Set(locationInput);
  }, [multiple, locationInput]);

  const sortedCities = useMemo(() => {
    if (!multiple || !selectedCountry) return filteredCities;
    const code = selectedCountry.code || "";
    const selected = [];
    const unselected = [];
    for (const city of filteredCities) {
      if (selectedSet.has(`${code} - ${city}`)) {
        selected.push(city);
      } else {
        unselected.push(city);
      }
    }
    return [...selected, ...unselected];
  }, [multiple, filteredCities, selectedCountry, selectedSet]);

  const displayCities = multiple ? sortedCities : filteredCities;

  const displayLabels = useMemo(() => {
    if (!multiple || !Array.isArray(locationInput)) return [];
    const seenCodes = new Set();
    return locationInput.map((loc) => {
      const dashIdx = loc.indexOf(" - ");
      if (dashIdx === -1) return loc;
      const code = loc.substring(0, dashIdx);
      if (seenCodes.has(code)) return loc.substring(dashIdx + 3);
      seenCodes.add(code);
      return loc;
    });
  }, [multiple, locationInput]);

  const visibleChips = hasValue && multiple ? locationInput.slice(0, MAX_VISIBLE_CHIPS) : [];
  const visibleLabels = hasValue && multiple ? displayLabels.slice(0, MAX_VISIBLE_CHIPS) : [];
  const overflowCount = hasValue && multiple ? locationInput.length - MAX_VISIBLE_CHIPS : 0;
  const overflowLabels = hasValue && multiple ? displayLabels.slice(MAX_VISIBLE_CHIPS) : [];

  return (
    <ClickAwayListener onClickAway={handleLocationDropdownClose}>
      <LocationPickerWrapper>
        <LocationInputField
          className={locationDropdownOpen ? "active" : ""}
          onClick={() => {
            if (!disabled) {
              setLocationDropdownOpen(!locationDropdownOpen);
            }
          }}
        >
          <img src={GoogleMapBg} alt="" className="map-bg" />
          <LocationOnOutlinedIcon className="location-icon" />
          {multiple ? (
            hasValue ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, overflow: "hidden" }}>
                {visibleChips.map((loc, i) => (
                  <Chip
                    key={loc}
                    label={visibleLabels[i]}
                    size="small"
                    onDelete={disabled ? undefined : (e) => {
                      e.stopPropagation();
                      handleLocationRemove(loc);
                    }}
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: colors.primary,
                      fontWeight: 500,
                      fontSize: "12px",
                      maxWidth: "140px",
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                      "& .MuiChip-deleteIcon": {
                        color: colors.primary,
                        fontSize: "16px",
                        "&:hover": { color: "#d32f2f" },
                      },
                    }}
                  />
                ))}
                {overflowCount > 0 && (
                  <Tooltip
                    title={overflowLabels.join(", ")}
                    placement="top"
                    arrow
                  >
                    <Chip
                      label={`+${overflowCount}`}
                      size="small"
                      sx={{
                        backgroundColor: colors.primary,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            ) : (
              <span className="location-placeholder">
                e.g., New York, London, Remote
              </span>
            )
          ) : locationInput ? (
            <span className="location-display">{locationInput}</span>
          ) : (
            <span className="location-placeholder">
              e.g., New York, London, Remote
            </span>
          )}
          {hasValue && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleLocationClear();
              }}
              sx={{
                color: "#999",
                padding: "4px",
                marginRight: "4px",
                "&:hover": {
                  color: colors.primary,
                  backgroundColor: "rgba(127, 29, 29, 0.08)",
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <KeyboardArrowDownIcon
            className={`dropdown-icon ${locationDropdownOpen ? "open" : ""}`}
          />
        </LocationInputField>

        {locationDropdownOpen && (
          <LocationDropdown>
            <Box className="country-section">
              {countryOptions.map((country) => (
                <Box
                  key={country.name}
                  className={`country-option ${
                    selectedCountry?.name === country.name ? "selected" : ""
                  }`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <img
                    src={country.flag}
                    alt={country.name}
                    className="country-flag"
                  />
                  {country.name}
                </Box>
              ))}
            </Box>

            <Box className="city-panel">
              {selectedCountry ? (
                <>
                  <Box className="city-search-wrapper">
                    <input
                      className="city-search-input"
                      type="text"
                      placeholder={`Search cities in ${selectedCountry.name}...`}
                      value={citySearchQuery}
                      onChange={(e) => setCitySearchQuery(e.target.value)}
                      autoFocus
                    />
                  </Box>

                  {loadingCities ? (
                    <Box className="loading-container">
                      <CircularProgress size={20} />
                      <span>Loading cities...</span>
                    </Box>
                  ) : displayCities.length > 0 ? (
                    <Box className="city-list">
                      <FixedSizeList
                        height={Math.min(displayCities.length * 40, 160)}
                        itemCount={displayCities.length}
                        itemSize={40}
                        width="100%"
                        overscanCount={10}
                        style={{ overflowX: "hidden", overflowY: "scroll", scrollbarWidth: "thin", scrollbarColor: `${colors.primary} #f5f5f5` }}
                      >
                        {({ index, style }) => {
                          const city = displayCities[index];
                          const isSelected = multiple && selectedSet.has(`${selectedCountry.code} - ${city}`);
                          return (
                            <Box
                              style={style}
                              className="city-option"
                              onClick={() => handleCitySelect(city)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                ...(isSelected && {
                                  backgroundColor: "#e3f2fd !important",
                                  fontWeight: 600,
                                }),
                              }}
                            >
                              {multiple && (
                                isSelected ? (
                                  <CheckBoxIcon sx={{ fontSize: 18, color: colors.primary, mr: 0.75, flexShrink: 0 }} />
                                ) : (
                                  <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: "#999", mr: 0.75, flexShrink: 0 }} />
                                )
                              )}
                              <span className="city-code">
                                {selectedCountry.code}
                              </span>
                              {city}
                            </Box>
                          );
                        }}
                      </FixedSizeList>
                    </Box>
                  ) : (
                    <Box className="no-results">No cities found</Box>
                  )}
                </>
              ) : (
                <Box className="select-country-hint">
                  ← Select a country to view cities
                </Box>
              )}
            </Box>
          </LocationDropdown>
        )}
      </LocationPickerWrapper>
    </ClickAwayListener>
  );
};

export default GlobalTalentLocationPicker;
